import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Envelope, CheckCircle, XCircle, Spinner, PaperPlaneTilt } from '@phosphor-icons/react';

/**
 * Settings section letting a user "Connect Gmail" so the CRM can send
 * emails on their behalf via Gmail OAuth.
 */
export default function GmailConnect() {
  const [status, setStatus] = useState(null); // null = loading, {connected: bool, email, ...}
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState('');

  const refresh = async () => {
    try {
      const { data } = await api.get('/oauth/gmail/status');
      setStatus(data);
    } catch (err) {
      setStatus({ connected: false });
    }
  };

  useEffect(() => {
    refresh();
    // Check URL params for callback redirect feedback
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      // Opened as an OAuth popup (e.g., from the bulk email drawer) — close self,
      // the opener polls /email/status and picks up the connection.
      if (window.opener && !window.opener.closed) {
        window.close();
        return;
      }
      setMsg('✓ Gmail connected!');
      setTimeout(() => setMsg(''), 4000);
      window.history.replaceState({}, '', '/settings');
    } else if (params.get('gmail') === 'error') {
      const reason = params.get('reason') || 'unknown_error';
      setMsg(`Connection failed: ${reason.replace(/_/g, ' ')}`);
      setTimeout(() => setMsg(''), 6000);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    setMsg('');
    try {
      // Pass our public origin so the backend can build the correct redirect URI.
      // CloudFlare/K8s ingress rewrites Host/Origin headers before they reach the backend,
      // so the frontend is the most reliable source of truth.
      const { data } = await api.get(`/oauth/gmail/start?frontend_origin=${encodeURIComponent(window.location.origin)}`);
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to start Gmail OAuth.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Gmail? You can reconnect anytime.')) return;
    setBusy(true);
    try {
      await api.post('/oauth/gmail/disconnect');
      setStatus({ connected: false });
      setMsg('Gmail disconnected.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Disconnect failed.');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setMsg('');
    try {
      const { data } = await api.post('/oauth/gmail/send-test', {});
      setMsg(`✓ Test email sent to ${data.sent_to}. Check your inbox!`);
      setTimeout(() => setMsg(''), 6000);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Test send failed.');
      setTimeout(() => setMsg(''), 6000);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-[#F5F5F0] border border-[#999] mb-2" data-testid="gmail-section">
      <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
        <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Email — Connect Gmail</h3>
      </div>
      <div className="px-4 py-3">
        {status === null ? (
          <div className="text-[12px] text-[#666] flex items-center gap-2">
            <Spinner size={14} className="animate-spin" /> Checking status…
          </div>
        ) : status.connected ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2 flex-1 min-w-[260px]">
              <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-[12px] text-[#333] font-semibold" data-testid="gmail-connected-email">
                  Connected as {status.email}
                </p>
                <p className="text-[11px] text-[#666] mt-0.5">
                  You can now send CRM emails through your own Gmail inbox.
                  Better deliverability, no spam folder issues.
                </p>
                {!status.has_refresh_token && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    ⚠ No refresh token saved. If sending starts failing, disconnect &amp; reconnect.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={sendTest}
                disabled={testing || busy}
                data-testid="gmail-send-test-btn"
                className="text-[12px] bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-50 text-black font-semibold px-3 py-1.5 rounded shadow-sm inline-flex items-center gap-1"
              >
                {testing ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />}
                Send test
              </button>
              <button
                onClick={disconnect}
                disabled={busy}
                data-testid="gmail-disconnect-btn"
                className="text-[12px] bg-white border border-[#999] hover:bg-[#F0F0F0] disabled:opacity-50 text-[#333] font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"
              >
                <XCircle size={12} weight="bold" /> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <p className="text-[12px] text-[#333]">
                <strong>Send emails through your own Gmail.</strong>
              </p>
              <p className="text-[11px] text-[#666] mt-0.5 leading-relaxed">
                When you connect Gmail, mail merges and one-off emails are sent
                from <em>your</em> address — better deliverability, no spam
                folder issues, replies land in your normal inbox.
              </p>
              <p className="text-[10px] text-[#888] italic mt-1">
                We only request <strong>send</strong> permission. We never read your inbox.
              </p>
            </div>
            <button
              onClick={connect}
              disabled={busy}
              data-testid="gmail-connect-btn"
              className="text-[12px] bg-[#0B5394] hover:bg-[#094180] disabled:opacity-50 text-white font-semibold px-4 py-1.5 rounded shadow-sm inline-flex items-center gap-1.5 shrink-0"
            >
              <Envelope size={14} weight="fill" />
              {busy ? 'Redirecting…' : 'Connect Gmail'}
            </button>
          </div>
        )}
        {msg && (
          <p className={`text-[11px] mt-2 ${msg.startsWith('✓') ? 'text-emerald-700' : 'text-red-600'}`} data-testid="gmail-msg">
            {msg}
          </p>
        )}
      </div>
    </div>
  );
}
